import React from 'react';
import classNames from 'classnames';

interface TabProps {
    id: string;
    label: string;
}

/**
 * Tab component - used as a child of Tabs to define individual tabs.
 * This component doesn't render anything itself; it's just used to pass props to the parent Tabs component.
 */
export const Tab: React.FC<TabProps> = () => null;

interface TabsProps {
    activeTab: string;
    onChange: (tabId: string) => void;
    children: React.ReactElement<TabProps> | React.ReactElement<TabProps>[];
}

/**
 * Tabs component for navigation between different content sections.
 * Buttons fill the full height of the tabs container.
 *
 * @example
 * ```tsx
 * <Tabs activeTab={activeTab} onChange={setActiveTab}>
 *   <Tab id="active" label="Active" />
 *   <Tab id="deleted" label="Deleted" />
 * </Tabs>
 * ```
 */
export const Tabs: React.FC<TabsProps> = ({ activeTab, onChange, children }) => {
    const [indicatorStyle, setIndicatorStyle] = React.useState<{ left: number; width: number } | null>(null);
    const [isTransitioning, setIsTransitioning] = React.useState(false);
    const tabRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map());
    const containerRef = React.useRef<HTMLDivElement>(null);
    const hoverBgRef = React.useRef<HTMLDivElement>(null);
    const previousActiveTabRef = React.useRef<string | null>(null);
    const transitionTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    // Extract tab data from children
    const tabs = React.Children.toArray(children).filter(
        (child): child is React.ReactElement<TabProps> =>
            React.isValidElement(child) && child.type === Tab
    ).map(child => ({
        id: child.props.id,
        label: child.props.label
    }));

    // Function to update indicator position
    const updateIndicatorPosition = React.useCallback(() => {
        const activeButton = tabRefs.current.get(activeTab);
        const container = containerRef.current;

        if (activeButton && container) {
            const containerRect = container.getBoundingClientRect();
            const buttonRect = activeButton.getBoundingClientRect();

            setIndicatorStyle({
                left: buttonRect.left - containerRect.left,
                width: buttonRect.width
            });
        }
    }, [activeTab]);

    // Update indicator position when active tab changes
    React.useEffect(() => {
        const isTabChange = previousActiveTabRef.current !== null && previousActiveTabRef.current !== activeTab;

        if (isTabChange) {
            // Clear any existing transition timeout
            if (transitionTimeoutRef.current) {
                clearTimeout(transitionTimeoutRef.current);
            }

            // Enable transition for tab change
            setIsTransitioning(true);
            updateIndicatorPosition();

            // Disable transition after animation completes (300ms)
            transitionTimeoutRef.current = setTimeout(() => {
                setIsTransitioning(false);
            }, 300);
        } else {
            // Initial render or programmatic update - no transition
            updateIndicatorPosition();
        }

        previousActiveTabRef.current = activeTab;
    }, [activeTab, updateIndicatorPosition]);

    // Watch for container resize and update indicator position (without transition)
    React.useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const resizeObserver = new ResizeObserver(() => {
            // Update position without transition on resize
            updateIndicatorPosition();
        });

        resizeObserver.observe(container);

        return () => {
            resizeObserver.disconnect();
        };
    }, [updateIndicatorPosition]);

    // Cleanup timeout on unmount
    React.useEffect(() => () => {
        if (transitionTimeoutRef.current) {
            clearTimeout(transitionTimeoutRef.current);
        }
    }, []);

    return (
        <div ref={containerRef} className="border border-neutral-100 rounded-lg flex relative">
            {/* Base background */}
            <div className="absolute inset-0 bg-neutral-50 rounded-lg" />

            {/* Hover background layer - covers entire container */}
            <div ref={hoverBgRef} className="absolute inset-0 bg-neutral-100 rounded-lg opacity-0 transition-opacity pointer-events-none z-[5]" />

            {/* Sliding background indicator */}
            {indicatorStyle && (
                <div
                    className={classNames(
                        `absolute top-0 bottom-0 bg-neutral-900 rounded-lg z-10`,
                        isTransitioning && `transition-all duration-300 ease-in-out`
                    )}
                    style={{
                        left: `${indicatorStyle.left}px`,
                        width: `${indicatorStyle.width}px`
                    }}
                />
            )}

            {tabs.map((tab) => {
                const isActive = activeTab === tab.id;

                return (
                    <button
                        key={tab.id}
                        ref={(el) => {
                            if (el) {
                                tabRefs.current.set(tab.id, el);
                            } else {
                                tabRefs.current.delete(tab.id);
                            }
                        }}
                        type="button"
                        onClick={() => onChange(tab.id)}
                        onMouseEnter={() => {
                            if (!isActive && hoverBgRef.current) {
                                hoverBgRef.current.style.opacity = `1`;
                            }
                        }}
                        onMouseLeave={() => {
                            if (hoverBgRef.current) {
                                hoverBgRef.current.style.opacity = `0`;
                            }
                        }}
                        className={classNames(
                            `relative z-20 flex-1 px-4 py-2 rounded-lg font-medium text-sm leading-5 tracking-[0.28px] focus-visible:outline-focus-ring`,
                            isActive ? `text-white` : `text-neutral-900`
                        )}
                    >
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );
};
